import type { FeatureFlagValue } from '@posthog/core';
import { PostHog } from 'posthog-js';
import { createContext, useContext } from 'react';

enum PostHogFeatureFlagMap {
  EnableIconPicker = 'enable-icon-picker',
  EnableAdditionalExpressOnboardingOptions = 'additional-express-onboarding-options-enabled',
  EnableAiWorkspaceCustomization = 'ai-workspace-customization',
  EnableAlphaApps = 'enable-alpha-apps',
  EnableAppBuilder = 'enable_app_builder',
  EnableBetaApps = 'enable-beta-apps',
  EnableCompanyImport = 'enable_company_import',
  EnableContextualActions = 'enable-contextual-actions',
  EnableContextBarVisualUpgrades = 'context_bar_visual_upgrades',
  EnableContextBarOtherPage = 'context_bar_other_pages',
  EnableIUAccessControl = 'iu_access_control',
  EnableMicrodepositVerification = 'enable-microdeposit-verification',
  EnableMessageAppNotification = 'message-app-notification',
  EnableMessageEditorInput = 'message-editor-input',
  EnableOtherMessagesEditor = 'enable-other-messages-editor',
  EnableDuplicateFileName = 'duplicate_file_name',
  EnableFilesAppPerformance = 'files-app-performance',
  EnableLoadFileChannels = 'details-page-file-channels-loading',
  EnableNewEditorFormatter = 'new-editor-formatter',
  EnableInvoiceAddressCustomFields = 'invoice_address_custom_fields',
  EnableAuditLogs = 'audit_logs',
  EnableAuditLogPlatform = 'audit_logs_platform',
  EnableFeatureAppVisibility = 'app_visibility_ocmc_support',
  EnableEventsApiDateFilters = 'events_api_date_filters',
  EnableInvoiceRefunds = 'enable_invoice_refunds',
  EnableMcpDashboardUi = 'mcp_dashboard_ui',
  EnableIterateOnProjectGithubActions = 'iterate_on_project_github_actions',
  EnableProductAgent = 'enable_product_agent',
}

export type FlagContextType = Record<
  'EnableManyCompanies' | keyof typeof PostHogFeatureFlagMap,
  boolean
>;

export const DefaultFlagsContext: FlagContextType = {
  // Permanently enabled
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: false,
  EnableAiWorkspaceCustomization: false,
  EnableAlphaApps: false,
  EnableAppBuilder: false,
  EnableBetaApps: false,
  EnableCompanyImport: false,
  EnableContextualActions: false,
  EnableContextBarOtherPage: false,
  EnableContextBarVisualUpgrades: false,
  EnableDuplicateFileName: false,
  EnableFilesAppPerformance: false,
  EnableIUAccessControl: false,
  EnableLoadFileChannels: false,
  EnableMessageAppNotification: false,
  EnableMessageEditorInput: false,
  EnableMicrodepositVerification: false,
  EnableNewEditorFormatter: false,
  EnableOtherMessagesEditor: false,
  EnableInvoiceAddressCustomFields: false,
  EnableInvoiceRefunds: false,
  EnableAuditLogs: false,
  EnableEventsApiDateFilters: false,
  EnableIconPicker: false,
  EnableFeatureAppVisibility: false,
  EnableMcpDashboardUi: false,
  EnableIterateOnProjectGithubActions: false,
  EnableProductAgent: false,
  EnableAuditLogPlatform: false,
};

/**
 * These flags are available on the server side, but are not used so we
 * purposefully exclude them
 */
type ClientOnlyFeatureFlagKeysType = never;

export const getServerFlagsContextValue = (
  posthogFlags: Record<PostHogFeatureFlagMap, FeatureFlagValue>,
): Record<
  Exclude<keyof FlagContextType, ClientOnlyFeatureFlagKeysType>,
  boolean
> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableIconPicker: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIconPicker],
  ),
  EnableAdditionalExpressOnboardingOptions: Boolean(
    posthogFlags[
      PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions
    ],
  ),
  EnableAiWorkspaceCustomization: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAiWorkspaceCustomization],
  ),
  EnableAlphaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAlphaApps]),
  EnableAppBuilder: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilder],
  ),
  EnableBetaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableBetaApps]),
  EnableCompanyImport: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableCompanyImport],
  ),
  EnableContextualActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextualActions],
  ),
  EnableContextBarVisualUpgrades: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextBarVisualUpgrades],
  ),
  EnableContextBarOtherPage: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextBarOtherPage],
  ),
  EnableIUAccessControl: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIUAccessControl],
  ),
  EnableMicrodepositVerification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMicrodepositVerification],
  ),
  EnableMessageAppNotification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMessageAppNotification],
  ),
  EnableMessageEditorInput: Boolean(posthogFlags['message-editor-input']),
  EnableOtherMessagesEditor: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableOtherMessagesEditor],
  ),
  EnableDuplicateFileName: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDuplicateFileName],
  ),
  EnableFilesAppPerformance: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFilesAppPerformance],
  ),
  EnableLoadFileChannels: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableLoadFileChannels],
  ),
  EnableNewEditorFormatter: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableNewEditorFormatter],
  ),
  EnableInvoiceAddressCustomFields: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableInvoiceAddressCustomFields],
  ),
  EnableInvoiceRefunds: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableInvoiceRefunds],
  ),
  EnableAuditLogs: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAuditLogs]),
  EnableFeatureAppVisibility: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFeatureAppVisibility],
  ),
  EnableEventsApiDateFilters: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableEventsApiDateFilters],
  ),
  EnableMcpDashboardUi: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMcpDashboardUi],
  ),
  EnableIterateOnProjectGithubActions: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions],
  ),
  EnableProductAgent: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableProductAgent],
  ),
  EnableAuditLogPlatform: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAuditLogPlatform],
  ),
});

export const getClientFlagsContextValue = (
  posthogFlags: PostHog,
): Record<keyof FlagContextType, boolean | undefined> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions,
  ),
  EnableAiWorkspaceCustomization: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAiWorkspaceCustomization,
  ),
  EnableAlphaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAlphaApps,
  ),
  EnableAppBuilder: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilder,
  ),
  EnableBetaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBetaApps,
  ),
  EnableCompanyImport: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableCompanyImport,
  ),
  EnableContextualActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextualActions,
  ),
  EnableContextBarVisualUpgrades: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextBarVisualUpgrades,
  ),
  EnableContextBarOtherPage: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextBarOtherPage,
  ),
  EnableIUAccessControl: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIUAccessControl,
  ),
  EnableMicrodepositVerification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMicrodepositVerification,
  ),
  EnableMessageAppNotification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageAppNotification,
  ),
  EnableMessageEditorInput: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageEditorInput,
  ),
  EnableOtherMessagesEditor: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableOtherMessagesEditor,
  ),
  EnableDuplicateFileName: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDuplicateFileName,
  ),
  EnableFilesAppPerformance: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFilesAppPerformance,
  ),
  EnableLoadFileChannels: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableLoadFileChannels,
  ),
  EnableNewEditorFormatter: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableNewEditorFormatter,
  ),
  EnableInvoiceAddressCustomFields: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableInvoiceAddressCustomFields,
  ),
  EnableInvoiceRefunds: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableInvoiceRefunds,
  ),
  EnableAuditLogs: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogs,
  ),
  EnableEventsApiDateFilters: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableEventsApiDateFilters,
  ),
  EnableIconPicker: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIconPicker,
  ),
  EnableFeatureAppVisibility: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFeatureAppVisibility,
  ),
  EnableMcpDashboardUi: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMcpDashboardUi,
  ),
  EnableIterateOnProjectGithubActions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIterateOnProjectGithubActions,
  ),
  EnableProductAgent: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableProductAgent,
  ),
  EnableAuditLogPlatform: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogPlatform,
  ),
});

export const FlagsContext = createContext(DefaultFlagsContext);

/**
 * Custom hook to access feature flags from the context. This abstracts away the
 * underlying implementation and allows components to easily check if a feature is enabled.
 */
export function useFeatureFlags(): FlagContextType {
  return useContext(FlagsContext);
}
